import { sendEmail } from '@/utils/emailjs'
import { settleCaptureDelivery } from '@/utils/captureDelivery'

/**
 * Store consent on the durable list and independently notify the inbox.
 * Success means request acceptance; prior unsubscribes stay suppressed. An inbox notification is not
 * proof of a stored subscriber or delivery of an email to that subscriber.
 * A deadline reports an unresolved request honestly; both deliveries continue.
 */

export type CaptureList = 'newsletter' | 'android_waitlist' | 'detox_challenge'

export interface CaptureEmailArgs {
  email: string
  /** What the person consented to. Decides the consent text stored on the row. */
  list: CaptureList
  /** Which surface they signed up on, for attribution. */
  source: string
  /** Optional free-text context — a barcode, a slug, a page path. */
  context?: string
  /** Params for the existing EmailJS notification, passed through untouched. */
  emailParams: Parameters<typeof sendEmail>[0]
}

export interface CaptureEmailResult {
  success: boolean
  /** Request accepted without revealing active membership. */
  accepted: boolean
  /** Inbox notification confirmed by the time this result was returned. */
  notified: boolean
  /** The request did not settle before the UI deadline; its write may continue. */
  pending: boolean
}

async function addToList(args: CaptureEmailArgs): Promise<boolean> {
  try {
    const res = await fetch('/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: args.email,
        list: args.list,
        source: args.source,
        context: args.context,
      }),
    })
    const data = await res.json().catch(() => null)
    // Acceptance does not disclose whether a row is active or suppressed.
    return res.ok && data?.ok === true && data?.accepted === true
  } catch {
    return false
  }
}

export async function captureEmail(args: CaptureEmailArgs): Promise<CaptureEmailResult> {
  const { accepted, notified, pending } = await settleCaptureDelivery(
    addToList(args),
    sendEmail(args.emailParams).then((result) => Boolean(result?.success)),
  )
  return { success: accepted, accepted, notified, pending }
}
